import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group';
import { cn } from '@zond/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import { AnimatePresence, motion, type HTMLMotionProps, type Transition } from 'motion/react';
import * as React from 'react';

const toggleVariants = cva(
  "cursor-pointer inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium hover:text-muted-foreground text-accent-foreground transition-[color,box-shadow] disabled:pointer-events-none disabled:opacity-50 data-[state=on]:text-accent-foreground [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0 focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] outline-none focus:outline-none aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive whitespace-nowrap",
  {
    variants: {
      type: {
        single: '',
        multiple: 'data-[state=on]:bg-accent',
      },
      variant: {
        default: 'bg-transparent',
        outline: 'border border-input bg-transparent shadow-xs',
      },
      size: {
        default: 'h-9 px-2 min-w-9',
        sm: 'h-8 px-1.5 min-w-8',
        lg: 'h-10 px-2.5 min-w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

// Define exact types for variant and size
type ToggleVariant = NonNullable<VariantProps<typeof toggleVariants>['variant']>;
type ToggleSize = NonNullable<VariantProps<typeof toggleVariants>['size']>;

type ToggleGroupContextValue = {
  type: 'single' | 'multiple';
  value: string | string[];
  variant?: ToggleVariant;
  size?: ToggleSize;
  transition?: Transition;
  activeClassName?: string;
};

const ToggleGroupContext = React.createContext<ToggleGroupContextValue | null>(null);

function useToggleGroupContext() {
  const context = React.useContext(ToggleGroupContext);
  if (!context) {
    throw new Error('useToggleGroupContext must be used within a ToggleGroup');
  }
  return context;
}

// Common props for both types
type CommonToggleGroupProps = {
  className?: string;
  variant?: ToggleVariant;
  size?: ToggleSize;
  transition?: Transition;
  activeClassName?: string;
  children?: React.ReactNode;
};

// Single Toggle Props
type SingleToggleGroupProps = CommonToggleGroupProps &
  Omit<React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Root>, 'type'> & {
    type: 'single';
    value?: string;
    defaultValue?: string;
    onValueChange?: (value: string) => void;
  };

// Multiple Toggle Props
type MultipleToggleGroupProps = CommonToggleGroupProps &
  Omit<React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Root>, 'type'> & {
    type: 'multiple';
    value?: string[];
    defaultValue?: string[];
    onValueChange?: (value: string[]) => void;
  };

const SingleToggleGroup = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Root>,
  SingleToggleGroupProps
>((props, ref) => {
  const {
    className,
    variant,
    size,
    children,
    transition = { type: 'spring', bounce: 0, stiffness: 200, damping: 25 },
    activeClassName,
    value,
    type = 'single',
    ...otherProps
  } = props;

  // Use memo for context to avoid rerenders
  const contextValue = React.useMemo(
    () => ({
      type,
      value: value || '',
      variant,
      size,
      transition,
      activeClassName,
    }),
    [type, value, variant, size, transition, activeClassName]
  );

  return (
    <ToggleGroupContext.Provider value={contextValue}>
      <ToggleGroupPrimitive.Root
        ref={ref}
        type="single"
        value={value}
        className={cn('flex items-center justify-center gap-1 relative', className)}
        {...otherProps}
      >
        {children}
      </ToggleGroupPrimitive.Root>
    </ToggleGroupContext.Provider>
  );
});

SingleToggleGroup.displayName = 'SingleToggleGroup';

const MultipleToggleGroup = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Root>,
  MultipleToggleGroupProps
>((props, ref) => {
  const {
    className,
    variant,
    size,
    children,
    transition = { type: 'spring', bounce: 0, stiffness: 200, damping: 25 },
    activeClassName,
    value,
    type = 'multiple',
    ...otherProps
  } = props;

  // Use memo for context to avoid rerenders
  const contextValue = React.useMemo(
    () => ({
      type,
      value: value || [],
      variant,
      size,
      transition,
      activeClassName,
    }),
    [type, value, variant, size, transition, activeClassName]
  );

  return (
    <ToggleGroupContext.Provider value={contextValue}>
      <ToggleGroupPrimitive.Root
        ref={ref}
        type="multiple"
        value={value}
        className={cn('flex items-center justify-center gap-1 relative', className)}
        {...otherProps}
      >
        {children}
      </ToggleGroupPrimitive.Root>
    </ToggleGroupContext.Provider>
  );
});

MultipleToggleGroup.displayName = 'MultipleToggleGroup';

// Export both types with a common name
const ToggleGroup = SingleToggleGroup as typeof SingleToggleGroup & {
  Multiple: typeof MultipleToggleGroup;
};
ToggleGroup.Multiple = MultipleToggleGroup;

type ToggleGroupItemProps = React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Item> &
  Omit<VariantProps<typeof toggleVariants>, 'type'> & {
    children?: React.ReactNode;
    buttonProps?: HTMLMotionProps<'button'>;
    spanProps?: React.HTMLAttributes<HTMLSpanElement>;
  };

const ToggleGroupItem = React.memo(
  React.forwardRef<React.ElementRef<typeof ToggleGroupPrimitive.Item>, ToggleGroupItemProps>(
    ({ className, children, variant, size, buttonProps, spanProps, value, ...props }, ref) => {
      const context = useToggleGroupContext();
      const {
        type,
        value: contextValue,
        variant: contextVariant,
        size: contextSize,
        transition,
        activeClassName,
      } = context;

      // Determine activity based on context type
      const isActive = React.useMemo(() => {
        if (!value) return false;

        if (type === 'single') {
          return value === contextValue;
        } else if (Array.isArray(contextValue)) {
          return contextValue.includes(value.toString());
        }
        return false;
      }, [type, value, contextValue]);

      // Use passed variant or from context
      const itemVariant = variant || contextVariant;
      const itemSize = size || contextSize;

      return (
        <ToggleGroupPrimitive.Item ref={ref} value={value} {...props} asChild>
          <motion.button
            initial={{ scale: 1 }}
            whileTap={{ scale: 0.9 }}
            {...buttonProps}
            className={cn('relative', buttonProps?.className)}
          >
            <span
              {...spanProps}
              data-state={isActive ? 'on' : 'off'}
              className={cn(
                'relative z-[1]',
                toggleVariants({
                  variant: itemVariant,
                  size: itemSize,
                  type,
                }),
                className,
                spanProps?.className
              )}
            >
              {children}
            </span>

            <AnimatePresence initial={false}>
              {isActive && type === 'single' && (
                <motion.span
                  layoutId="active-toggle-group-item"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={transition}
                  className={cn('absolute inset-0 z-0 rounded-md bg-muted', activeClassName)}
                />
              )}
            </AnimatePresence>
          </motion.button>
        </ToggleGroupPrimitive.Item>
      );
    }
  )
);
ToggleGroupItem.displayName = ToggleGroupPrimitive.Item.displayName;

export {
  ToggleGroup,
  ToggleGroupItem,
  type SingleToggleGroupProps,
  type MultipleToggleGroupProps,
  type ToggleGroupItemProps,
};
